import type { GameView } from '../types';
import { AppScreen } from './AppScreen';
import { ShareIcon } from './icons';

interface LobbyScreenProps {
  game: GameView | null;
  gameCode: string | null;
  isHost: boolean;
  onShare: () => void;
  onStart: () => void;
  onLeave: () => void;
}

export function LobbyScreen({ game, gameCode, isHost, onShare, onStart, onLeave }: LobbyScreenProps) {
  const players = game?.players ?? [];
  const count = players.length;

  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 flex items-center justify-between pt-[calc(env(safe-area-inset-top)+14px)] pb-1.5 px-4">
        <button
          onClick={onLeave}
          aria-label="Leave lobby"
          className="min-h-[44px] min-w-[44px] flex items-center justify-center text-atlas-ink dark:text-atlas-ink-dark"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className="font-serif text-[17px] font-semibold text-atlas-ink dark:text-atlas-ink-dark">Game Lobby</span>
        <div className="w-[44px]" />
      </div>

      <div className="flex-1 flex flex-col px-6 py-6 gap-4">
        <div className="rounded-2xl border-[1.5px] border-dashed border-atlas-brass dark:border-atlas-brass-dark bg-atlas-surface dark:bg-atlas-surface-dark p-5 flex flex-col items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Game Code
          </span>
          <span className="font-mono text-[32px] font-semibold tracking-[0.25em] text-atlas-brass dark:text-atlas-brass-dark">
            {gameCode ?? '------'}
          </span>
          <button
            onClick={onShare}
            className="w-full h-11 rounded-xl border-[1.5px] border-atlas-ink dark:border-atlas-ink-dark text-atlas-ink dark:text-atlas-ink-dark font-semibold text-[15px] flex items-center justify-center gap-2 hover:bg-atlas-bg dark:hover:bg-atlas-bg-dark transition-colors"
          >
            <ShareIcon className="w-4 h-4" />
            Share via Message
          </button>
        </div>

        <div className="bg-atlas-surface dark:bg-atlas-surface-dark rounded-2xl p-4 shadow-sm">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-3">
            Players ({count})
          </h3>
          <ul className="flex flex-col gap-2">
            {players.map((p, i) => (
              <li
                key={`${p.name}-${i}`}
                className="flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-atlas-bg dark:bg-atlas-bg-dark"
              >
                <span className="text-[15px] font-medium text-atlas-ink dark:text-atlas-ink-dark">{p.name}</span>
                {p.isHost && (
                  <span className="text-[11px] font-semibold text-atlas-brass dark:text-atlas-brass-dark border border-atlas-brass dark:border-atlas-brass-dark rounded-full px-2 py-0.5">
                    Host
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto flex flex-col gap-2">
          <button
            onClick={onStart}
            disabled={!isHost}
            className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Start Game
          </button>
          <p className="text-center text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            {isHost ? `${count} player${count !== 1 ? 's' : ''} in lobby` : 'Waiting for the host to start…'}
          </p>
        </div>
      </div>
    </AppScreen>
  );
}
